import { Modal } from 'antd';
import { useAtomValue, useSetAtom } from 'jotai';
import React, {
	ReactNode,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Calendar,
	DateLocalizer,
	EventProps,
	ToolbarProps,
	Views,
} from 'react-big-calendar';
import withDragAndDrop, {
	EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';

import noOverlapNoExtend from '../core/customCalendarAlgorithm';
import { focusResaAtom, rbcLocalizerAtom } from '../store/core';
import { ResaLocation, allLocationsAtom } from '../store/location';
import {
	Reservation,
	allReservationsAtom,
	mergeReservations,
} from '../store/reservation';

import {
	ColumnHeightOutlined,
	DragOutlined,
	EditOutlined,
	SelectOutlined,
} from '@ant-design/icons';
import useModal from 'antd/es/modal/useModal';
import classNames from 'classnames';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import './ResaCalendar.scss';
import { EventRenderer } from './ResaCalendarEvent';
import { DayColumnWrapper } from './ResaCalendarBackground';
import { WeekEndCustomView } from './WeekEndCustomView';

const InteractiveCalendar = withDragAndDrop(Calendar);
export type ResaCalendarEvent = {
	type: 'resa';

	id: string;
	start: Date;
	end: Date;
	allDay: false;
	resource: Reservation;
};
export type MandatoryCalendarEvent = {
	type: 'mandatory';
	start: Date;
	end: Date;
	info: string;
};
export type KnownResaCalendarEvent = ResaCalendarEvent & {
	resource: { anonymized: false };
};

export type CalendarEvent = ResaCalendarEvent | MandatoryCalendarEvent;

export default function ResaCalendar(props: {
	reservations: Reservation[];
	sideReservations?: Reservation[];
	defaultLocation?: ResaLocation;
	defaultUsers?: number[];
	availabilitySlots?: { start: Date; end: Date; label?: ReactNode }[];
	showCancelled?: boolean;
	modalContainer: HTMLElement;
	displayOnlyOpen?: boolean;
	onDetailOpen?: () => void;
	EventEditor: (props: EventEditorProps) => ReactNode;
	affluence?: Map<number, number>;
}) {
	const focusResa = useAtomValue(focusResaAtom);

	const setReservations = useSetAtom(allReservationsAtom);
	const allLocations = useAtomValue(allLocationsAtom);
	const defaultLocation = useMemo(
		() => props.defaultLocation ?? allLocations[0],
		[props.defaultLocation, allLocations],
	);
	const [modal] = useModal();

	const localizer = useAtomValue(rbcLocalizerAtom);

	// start and end of day
	// !important, min and max are time-only date oblects, their date MUST BE TODAY and only h/m/s matter
	const min = useMemo(() => {
		if (
			props.displayOnlyOpen &&
			props.availabilitySlots &&
			props.availabilitySlots.length > 0
		) {
			const min = props.availabilitySlots.reduce((min: Date | undefined, x) => {
				const timeonlyX = localizer.merge(new Date(), x.start)!;
				if (!min) return timeonlyX;
				return localizer.min(min, timeonlyX);
			}, undefined);
			if (min) return localizer.startOf(min, 'hours');
		}
		return localizer.startOf(new Date(), 'day');
	}, [props.displayOnlyOpen, props.availabilitySlots]);
	const max = useMemo(() => {
		if (
			props.displayOnlyOpen &&
			props.availabilitySlots &&
			props.availabilitySlots.length > 0
		) {
			const max = props.availabilitySlots.reduce((max: Date | undefined, x) => {
				const timeonlyX = localizer.merge(new Date(), x.end)!;
				if (!max) return timeonlyX;
				return localizer.max(max, timeonlyX);
			}, undefined);
			if (max)
				return max.getMinutes() > 0 ? localizer.endOf(max, 'hours') : max;
		}
		return localizer.endOf(new Date(), 'day');
	}, [props.displayOnlyOpen, props.availabilitySlots]);
	// console.log(
	// 	min,
	// 	max,
	// 	localizer.getTimezoneOffset(min),
	// 	localizer.getTimezoneOffset(max),
	// 	localizer.add(min, -1, 'day'),
	// );

	const events = useMemo(
		() =>
			(props.reservations ?? [])
				.filter(
					(r) =>
						(props.showCancelled || r.anonymized || r.canceledBy == null) &&
						localizer.merge(new Date(), r.start)! < max &&
						localizer.merge(new Date(), r.end)! > min,
				)
				.map((r) => ({
					type: 'resa',
					id: `resa_${r.id}`,
					start: r.start,
					end: r.end,
					allDay: false,
					resource: r,
				})),
		[props.reservations, min, max],
	);
	const backgroundEvents = useMemo(
		() =>
			(props.sideReservations ?? [])
				.filter(
					(r) =>
						(props.showCancelled ||
							!('canceledBy' in r) ||
							r.canceledBy == null) &&
						localizer.merge(new Date(), r.start)! < max &&
						localizer.merge(new Date(), r.end)! > min,
				)
				.map((r) => ({
					type: 'resa',
					id: `resa_${r.id}`,
					start: r.start,
					end: r.end,
					allDay: false,
					resource: r,
				})),
		[props.sideReservations, min, max],
	);

	const [selectedEvent, setSelectedEvent] = useState<ResaCalendarEvent | null>(
		null,
	);

	const onCreateNew = useCallback(
		(start: Date, end: Date) => {
			setSelectedEvent({
				type: 'resa',
				id: 'new',
				allDay: false,
				start,
				end,
				resource: {
					anonymized: false,
					id: 'new', // IMPORTANT this is how we know this is a transient object in the frontend, real ID will be given after commit
					start,
					end,
					locationId: defaultLocation?.ID,
					users:
						props.defaultUsers?.map((userId, i) => ({
							userId,
							accepted: i == 0,
						})) ?? [],
					created: new Date(Date.now()), // fake
					updated: new Date(Date.now()), // fake
					canceledBy: null,
					approved: null,
					notes: null,
				},
			});
		},
		[defaultLocation, props.defaultUsers],
	);
	const handleSelectSlot = useCallback(
		({ start: _start, end: _end }: { start: Date; end: Date }) => {
			const start = typeof _start === 'string' ? new Date(_start) : _start;
			const end = typeof _end === 'string' ? new Date(_end) : _end;

			onCreateNew(start, end);
		},
		[onCreateNew],
	);
	const handleSelectEvent = useCallback(
		(event: CalendarEvent) => {
			if (event.type == 'mandatory') return;
			setSelectedEvent(event);
		},
		[setSelectedEvent],
	);
	useEffect(() => {
		if (selectedEvent) props.onDetailOpen?.();
	}, [selectedEvent]);

	const commitEvent = useCallback(
		async (editedEvent: KnownResaCalendarEvent) => {
			// if(!focusResa.isAdmin && !editedEvent.resource.anonymized && editedEvent.resource.approved != null){
			//     await modal.info({
			//         title: "Vous ne pouvez pas modifier un evenment approuvé, vous devez l'annuler et en récréer un ou contacter le staff."
			//     });
			//     return;
			// }
			// const confirm = await modal.confirm({
			//     title: 'Etes-vous sur.e ?',
			//     content: '',
			//     okText: 'Oui',
			//     okType: 'danger',
			//     cancelText: 'Non, annuler'
			// });
			// if(!confirm) return;

			const rollback = selectedEvent;
			setSelectedEvent(null);
			setReservations((all) => mergeReservations(all, [editedEvent.resource]));

			if (editedEvent.resource.locationId == null)
				// TODO ?
				return;

			try {
				if (editedEvent.id == 'new') {
					const { id } = await focusResa.createReservation({
						locationId: editedEvent.resource.locationId,
						dateFrom: editedEvent.start,
						dateTo: editedEvent.end,
						userIds: editedEvent.resource.users.map((u) => u.userId),
						notes: editedEvent.resource.notes ?? '',
					});
					setReservations((all) =>
						mergeReservations(
							all.filter((r) => r.id != 'new'),
							[
								{
									...editedEvent.resource,
									id: `${id}`,
								},
							],
						),
					);
				} else {
					await focusResa.updateReservation({
						resaId: editedEvent.resource.id,
						dateFrom: editedEvent.start,
						dateTo: editedEvent.end,
						locationId: editedEvent.resource.locationId,
						notes: editedEvent.resource.notes ?? undefined,
						...(selectedEvent != null
							? {
									addUsers: editedEvent.resource.users
										.map((u) => u.userId)
										.filter(
											(id) =>
												!selectedEvent?.resource.users.some(
													(u) => u.userId == id,
												),
										),
									removeUsers: selectedEvent?.resource.users
										.map((u) => u.userId)
										.filter(
											(id) =>
												!editedEvent.resource.users.some((u) => u.userId == id),
										),
								}
							: {}),
					});
					setReservations((all) =>
						mergeReservations(
							all.filter((r) => r.id != 'new'),
							[
								{
									...editedEvent.resource,
									updated: new Date(),
								},
							],
						),
					);
				}
			} catch (e) {
				setReservations((all) =>
					mergeReservations(
						all.filter((r) => r.id != 'new'),
						rollback ? [rollback.resource] : [],
					),
				);
			}
		},
		[focusResa, selectedEvent, modal],
	);

	const moveEvent = useCallback(
		({
			event,
			start: _start,
			end: _end,
			isAllDay: droppedOnAllDaySlot = false,
		}: EventInteractionArgs<CalendarEvent>) => {
			if (event.type == 'mandatory' || event.resource.anonymized) return;
			if (droppedOnAllDaySlot) return;

			const start = typeof _start === 'string' ? new Date(_start) : _start;
			const end = typeof _end === 'string' ? new Date(_end) : _end;

			commitEvent({
				...event,
				start,
				end,
				resource: {
					...event.resource,
					start,
					end,
				},
			});
		},
		[],
	);

	const resizeEvent = useCallback(
		({
			event,
			start: _start,
			end: _end,
		}: EventInteractionArgs<CalendarEvent>) => {
			if (event.type == 'mandatory' || event.resource.anonymized) return;
			const start = typeof _start === 'string' ? new Date(_start) : _start;
			const end = typeof _end === 'string' ? new Date(_end) : _end;

			commitEvent({
				...event,
				start,
				end,
				resource: {
					...event.resource,
					start,
					end,
				},
			});
		},
		[],
	);

	const mandatoryEvents = useMemo(
		() =>
			focusResa.mandatoryEvents
				.filter(
					(e) =>
						localizer.merge(new Date(), e.start)! < max &&
						localizer.merge(new Date(), e.end)! > min,
				)
				.map((e) => ({
					...e,
					type: 'mandatory',
				})),
		[focusResa, min, max],
	);

	return (
		<CalendarSubComponentsContext.Provider
			value={{
				onCreateNew,
				openHours: props.availabilitySlots ?? [],
				affluence: props.affluence,
				min,
				max,
			}}
		>
			<InteractiveCalendar
				localizer={localizer}
				className="rbc-no-fullday focus-resa-rbc"
				defaultDate={focusResa.dates.start}
				views={{
					day: true,
					week: WeekEndCustomView,
				}}
				defaultView={Views.DAY}
				min={min}
				max={max}
				showMultiDayTimes={true}
				events={[...mandatoryEvents, ...events]}
				backgroundEvents={backgroundEvents}
				selectable={true}
				selected={selectedEvent}
				onSelectEvent={handleSelectEvent}
				onSelectSlot={handleSelectSlot}
				onEventDrop={moveEvent}
				onEventResize={resizeEvent}
				dayLayoutAlgorithm={noOverlapNoExtend}
				eventPropGetter={(e: CalendarEvent) => ({
					className:
						e.type == 'mandatory'
							? 'mandatory'
							: e.type == 'resa' && !e.resource.anonymized
								? 'expandable'
								: 'static',
				})}
				components={ResaCalendarComponents}
			/>
			<CalendarNotice showAffluence={props.affluence !== undefined} />
			<Suspense>
				{selectedEvent && (
					<EventModal
						event={selectedEvent}
						onCommit={commitEvent}
						onClose={() => setSelectedEvent(null)}
						Content={props.EventEditor}
						modalContainer={props.modalContainer}
					/>
				)}
			</Suspense>
		</CalendarSubComponentsContext.Provider>
	);
}

const TimeSlot = (props: {
	value: Date;
	resource: any;
	children: ReactNode;
}) => {
	const { affluence } = useContext(CalendarSubComponentsContext);
	const [percent, cl] = useMemo(() => {
		if (!affluence) return [0, 'na'];
		let f = -1;
		const t = props.value.getTime();
		affluence.forEach((value, key) => {
			if (key <= t && key + 60 * 60 * 1000 > t) {
				f = value;
			}
		});
		if (f < 0) return [0, 'na'];
		if (f < 0.5) return [f, 'low'];
		if (f < 0.75) return [f, 'mid'];
		if (f < 1) return [f, 'high'];
		return [1, 'full'];
	}, [affluence, props.value]);

	const intl = useIntl();

	return React.Children.map(props.children, (c) => (
		<div style={{ flex: '1 0 0', position: 'relative', width: '100%' }}>
			<div
				className={classNames('frequency-indicator', cl)}
				title={intl.formatMessage(
					{
						defaultMessage: 'Fréquentation: {percent}%',
						id: 'yd/EhP',
					},
					{
						percent: intl.formatNumber(percent * 100, {
							maximumFractionDigits: 0,
						}),
					},
				)}
			/>
			{c}
		</div>
	));
};

type EventEditorProps = {
	event: ResaCalendarEvent;
	commit: (e: ResaCalendarEvent) => void;
	close: () => void;
};

const EventModal = (props: {
	event: ResaCalendarEvent;
	Content: (props: EventEditorProps) => ReactNode;
	modalContainer: HTMLElement;
	onClose: () => void;
	onCommit: (e: ResaCalendarEvent) => void;
}) => {
	return (
		<Modal
			open={true}
			getContainer={props.modalContainer}
			footer={false}
			onCancel={props.onClose}
			width={700}
		>
			<Suspense>
				<props.Content
					event={props.event}
					close={props.onClose}
					commit={props.onCommit}
				/>
			</Suspense>
		</Modal>
	);
};

export const CalendarSubComponentsContext = React.createContext({
	onCreateNew: (start: Date, end: Date) => {
		console.log(start, end);
	},
	openHours: [] as { start: Date; end: Date; label?: ReactNode }[],
	affluence: undefined as Map<number, number> | undefined,
	min: new Date(),
	max: new Date(),
});

const FocusCalendarToolbar = (
	props: ToolbarProps & { localizer: DateLocalizer },
) => {
	const focusResa = useAtomValue(focusResaAtom);
	const { onCreateNew, openHours } = useContext(CalendarSubComponentsContext);

	const onNew = () => {
		const start =
			openHours?.length > 0
				? openHours[0].start
				: props.localizer.merge(props.date, new Date(8 * 3600000))!;
		onCreateNew(start, props.localizer.add(start, 1, 'hours'));
	};

	return (
		<div className="rbc-toolbar">
			<span className={classNames('rbc-btn-group', 'day-selector-group')}>
				{props.localizer
					.range(focusResa.dates.start, focusResa.dates.end)
					.map((d) => {
						return (
							<button
								type="button"
								key={d.getTime()}
								onClick={() => props.onNavigate('DATE', d)}
								disabled={props.localizer.eq(d, props.date, 'day')}
							>
								<span style={{ textTransform: 'capitalize' }}>
									<FormattedDate timeZone="+02:00" value={d} weekday="long" day="numeric" />
								</span>
							</button>
						);
					})}
			</span>
			<span className={classNames('rbc-btn-group', 'new-event-group')}>
				<button onClick={onNew}>
					<FormattedMessage defaultMessage="+ Nouveau" id="GW3zMJ" />
				</button>
			</span>
		</div>
	);
};

const ResaCalendarComponents = {
	toolbar: FocusCalendarToolbar,
	dayColumnWrapper: DayColumnWrapper,
	event: EventRenderer as (__0: EventProps<object>) => JSX.Element,
	timeSlotWrapper: TimeSlot,
};

const CalendarNotice = (props: { showAffluence: boolean }) => {
	return (
		<div className="calendar-notice">
			{props.showAffluence && (
				<div>
					<FormattedMessage
						defaultMessage="Fréquentation: {low} Faible {mid} Moyenne {high} Elevée {full} Très élevée"
						id="QIRUI6"
						values={{
							low: <span className="frequency-indicator low" />,
							mid: <span className="frequency-indicator mid" />,
							high: <span className="frequency-indicator high" />,
							full: <span className="frequency-indicator full" />,
						}}
					/>
				</div>
			)}
			<div>
				<FormattedMessage
					defaultMessage="{icon} Clique/Drag-and-drop sur le fond du calendrier: ajouter une réservation"
					id="wBQG/A"
					values={{ icon: <SelectOutlined /> }}
				/>
			</div>
			<div>
				<FormattedMessage
					defaultMessage="{icon} Clique sur un encart: éditer la réservation"
					id="rAh9Bx"
					values={{ icon: <EditOutlined /> }}
				/>
			</div>
			<div>
				<FormattedMessage
					defaultMessage="{icon} Déplacer un encart: déplacer la réservation"
					id="Gf6FE+"
					values={{ icon: <DragOutlined /> }}
				/>
			</div>
			<div>
				<FormattedMessage
					defaultMessage="{icon} Agrandir/réduire un encart: modifier la date de début/fin de la réservation"
					id="VbRv4U"
					values={{ icon: <ColumnHeightOutlined /> }}
				/>
			</div>
		</div>
	);
};
